package one.devsbox.www.ui.widgets

import androidx.compose.animation.AnimatedVisibility
import androidx.compose.animation.core.*
import androidx.compose.animation.fadeIn
import androidx.compose.animation.fadeOut
import androidx.compose.foundation.layout.*
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.rotate
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.lerp
import androidx.compose.ui.graphics.vector.ImageVector
import androidx.compose.ui.platform.LocalDensity
import androidx.compose.ui.unit.IntOffset
import androidx.compose.ui.unit.IntRect
import androidx.compose.ui.unit.IntSize
import androidx.compose.ui.unit.LayoutDirection
import androidx.compose.ui.unit.dp
import androidx.compose.ui.window.Popup
import androidx.compose.ui.window.PopupPositionProvider
import androidx.compose.ui.window.PopupProperties
import compose.assets.Svg
import compose.assets.svg.Plus
import one.devsbox.www.provider.LocalDisplayProvider
import org.jetbrains.compose.resources.StringResource
import org.jetbrains.compose.resources.stringResource

@OptIn(ExperimentalMaterial3ExpressiveApi::class)
@Composable
fun BoxScope.ContentActions(
    contentActions: List<ScreenContentAction>,
    colorInactive: Color,
    colorActive: Color
) {
    if (contentActions.isEmpty()) return

    val dp = LocalDisplayProvider.current.state
    val isMobileOrTablet = dp.mobile || dp.tablet

    if (contentActions.size == 1) {
        val action = contentActions.first()
        if (isMobileOrTablet) {
            SmallExtendedFloatingActionButton(
                text = { Text(stringResource(action.text)) },
                icon = { Icon(action.icon, null) },
                containerColor = if (action.color != Color.Unspecified) action.color else colorActive,
                modifier = Modifier
                    .align(Alignment.BottomEnd)
                    .padding(bottom = 24.dp, end = 24.dp),
                onClick = action.onClick
            )
        } else {
            ExtendedFloatingActionButton(
                text = { Text(stringResource(action.text)) },
                icon = { Icon(action.icon, null) },
                containerColor = if (action.color != Color.Unspecified) action.color else colorActive,
                modifier = Modifier
                    .align(Alignment.BottomEnd)
                    .padding(bottom = 24.dp, end = 24.dp),
                onClick = action.onClick
            )
        }
        return
    }

    var expanded by remember { mutableStateOf(false) }

    // Use a natural Spring or Easing for the rotation
    val angle by animateFloatAsState(
        targetValue = if (expanded) 45F else 0F,
        animationSpec = spring(stiffness = Spring.StiffnessMediumLow),
        label = "FabIconRotation"
    )

    Column(
        modifier = Modifier
            .align(Alignment.BottomEnd)
            .padding(bottom = 24.dp, end = 24.dp),
        horizontalAlignment = Alignment.End,
        verticalArrangement = Arrangement.spacedBy(16.dp)
    ) {
        Box(contentAlignment = Alignment.Center) {
            if (expanded) {
                val density = LocalDensity.current
                val spacingPx = remember(density) { with(density) { 16.dp.roundToPx() } }

                Popup(
                    popupPositionProvider = remember(spacingPx) {
                        object : PopupPositionProvider {
                            override fun calculatePosition(
                                anchorBounds: IntRect,
                                windowSize: IntSize,
                                layoutDirection: LayoutDirection,
                                popupContentSize: IntSize
                            ): IntOffset {
                                val x = anchorBounds.right - popupContentSize.width
                                val y = anchorBounds.top - popupContentSize.height - spacingPx
                                return IntOffset(x, y)
                            }
                        }
                    },
                    onDismissRequest = { expanded = false },
                    properties = PopupProperties(
                        focusable = true,
                        dismissOnClickOutside = true,
                        dismissOnBackPress = true
                    )
                ) {
                    var visible by remember { mutableStateOf(false) }
                    LaunchedEffect(Unit) {
                        visible = true
                    }

                    Column {
                        AnimatedVisibility(
                            visible = visible,
                            enter = fadeIn(animationSpec = tween(durationMillis = 200, easing = FastOutSlowInEasing)),
                            exit = fadeOut(animationSpec = tween(durationMillis = 150, easing = FastOutSlowInEasing))
                        ) {
                            Column(
                                horizontalAlignment = Alignment.End,
                                verticalArrangement = Arrangement.spacedBy(12.dp)
                            ) {
                                contentActions.forEach { action ->
                                    if (isMobileOrTablet) {
                                        SmallExtendedFloatingActionButton(
                                            text = { Text(stringResource(action.text)) },
                                            icon = { Icon(action.icon, null) },
                                            containerColor = if (action.color != Color.Unspecified) action.color else colorActive,
                                            onClick = {
                                                expanded = false
                                                action.onClick()
                                            }
                                        )
                                    } else {
                                        ExtendedFloatingActionButton(
                                            text = { Text(stringResource(action.text)) },
                                            icon = { Icon(action.icon, null) },
                                            containerColor = if (action.color != Color.Unspecified) action.color else colorActive,
                                            onClick = {
                                                expanded = false
                                                action.onClick()
                                            }
                                        )
                                    }
                                }
                            }
                        }
                    }
                }
            }

            // The Anchor Toggle Button
            ToggleFloatingActionButton(
                checked = expanded,
                onCheckedChange = { expanded = it },
                containerColor = { progress ->
                    lerp(
                        start = colorInactive,
                        stop = colorActive,
                        fraction = progress
                    )
                }
            ) {
                Icon(
                    imageVector = Svg.Plus,
                    contentDescription = "content actions",
                    modifier = Modifier.rotate(angle),
                    tint = lerp(
                        start = contentColorFor(colorInactive),
                        stop = contentColorFor(colorActive),
                        fraction = if (expanded) 1f else 0f
                    )
                )
            }
        }
    }
}

data class ScreenContentAction(
    val text: StringResource,
    val color: Color = Color.Unspecified,
    val icon: ImageVector,
    val onClick: () -> Unit
)